import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import org.w3c.dom.HTMLScriptElement

// SG_SUPABASE_URL / SG_ANON_KEY / BL_API_BASE come from the generated Config.kt (see
// build.gradle.kts). A production build points these at Supabase Cloud + the live bowlingliga
// team API without touching source; dev defaults target the local SG stack + production BL API.

// Sunny Gang's fixed identity in bowlingliga (see docs/IMPLEMENTATION_PLAN.md §0).
const val SG_TEAM_ID = "922b24a3-1280-43a2-960e-7c9d5eb57fea"

val httpClient = HttpClient(Js) {
    expectSuccess = true
    install(ContentNegotiation) {
        json(Json { ignoreUnknownKeys = true })
    }
}

// ---------------------------------------------------------------------------------------------
// Request infrastructure — ported from bowlingliga's Api.kt (singleFlight + two-tier ETag cache).
// The bowlingliga team API tags cache-backed responses with an ETag, so repeat requests for
// unchanged data come back as a bodyless 304 and skip the payload transfer.
// ---------------------------------------------------------------------------------------------

private val inFlightScope = kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.SupervisorJob())
private val inFlightRequests = mutableMapOf<String, kotlinx.coroutines.Deferred<*>>()

/** Coalesces concurrent identical GETs into one network call (not a cache — nothing is retained). */
@Suppress("UNCHECKED_CAST")
private suspend fun <T> singleFlight(key: String, block: suspend () -> T): T {
    (inFlightRequests[key] as? kotlinx.coroutines.Deferred<T>)?.let { return it.await() }
    val deferred = inFlightScope.async { block() }
    inFlightRequests[key] = deferred
    try {
        return deferred.await()
    } finally {
        if (inFlightRequests[key] === deferred) inFlightRequests.remove(key)
    }
}

private val etagStore = mutableMapOf<String, Pair<String, String>>()
private const val ETAG_NS = "etag:"
private val apiErrorJson = Json { ignoreUnknownKeys = true }

/** The `?v=<hash>` versionStaticAssets stamps onto composeApp.js at build time; "dev" when absent.
 *  Namespaces the persistent cache per build so a changed DTO shape can't be re-parsed from a stale body. */
private val buildVersion: String by lazy {
    try {
        val scripts = document.getElementsByTagName("script")
        var v = "dev"
        for (i in 0 until scripts.length) {
            val src = (scripts.item(i) as? HTMLScriptElement)?.src ?: continue
            if (src.contains("composeApp.js")) {
                val q = src.substringAfter("?v=", "")
                if (q.isNotEmpty()) { v = q.substringBefore("&"); break }
            }
        }
        v
    } catch (_: Throwable) { "dev" }
}

private fun etagLsKey(reqKey: String) = "${ETAG_NS}v$buildVersion:$reqKey"

@Serializable private data class EtagEntry(val e: String, val b: String)

private var etagPruned = false
private fun pruneStaleEtagEntries() {
    if (etagPruned) return
    etagPruned = true
    try {
        val keep = "${ETAG_NS}v$buildVersion:"
        (0 until localStorage.length)
            .mapNotNull { localStorage.key(it) }
            .filter { it.startsWith(ETAG_NS) && !it.startsWith(keep) }
            .forEach { localStorage.removeItem(it) }
    } catch (_: Throwable) { /* storage unavailable/disabled -- ignore */ }
}

private fun etagGet(key: String): Pair<String, String>? {
    etagStore[key]?.let { return it }
    return try {
        val raw = localStorage.getItem(etagLsKey(key)) ?: return null
        val entry = apiErrorJson.decodeFromString(EtagEntry.serializer(), raw)
        (entry.e to entry.b).also { etagStore[key] = it }
    } catch (_: Throwable) { null }
}

private fun etagPut(key: String, etag: String, body: String) {
    etagStore[key] = etag to body
    val lsKey = etagLsKey(key)
    val value = apiErrorJson.encodeToString(EtagEntry.serializer(), EtagEntry(etag, body))
    try {
        localStorage.setItem(lsKey, value)
    } catch (_: Throwable) {
        try {
            (0 until localStorage.length).mapNotNull { localStorage.key(it) }
                .filter { it.startsWith(ETAG_NS) }
                .forEach { localStorage.removeItem(it) }
            localStorage.setItem(lsKey, value)
        } catch (_: Throwable) { /* give up on persistence; memory tier still holds it */ }
    }
}

/** Conditional GET against the bowlingliga team API: resends the last ETag as If-None-Match and
 *  returns the cached body on a 304. */
private suspend inline fun <reified T> conditionalGet(
    key: String,
    url: String,
    noinline block: HttpRequestBuilder.() -> Unit = {},
): T {
    pruneStaleEtagEntries()
    val stored = etagGet(key)
    val response = httpClient.get(url) {
        expectSuccess = false
        block()
        stored?.let { (etag, _) -> header(HttpHeaders.IfNoneMatch, etag) }
    }
    if (response.status.value == 304 && stored != null) {
        return apiErrorJson.decodeFromString(stored.second)
    }
    if (!response.status.isSuccess()) {
        throw ResponseException(response, response.bodyAsText())
    }
    val bodyText = response.bodyAsText()
    response.headers[HttpHeaders.ETag]?.let { etag -> etagPut(key, etag, bodyText) }
    return apiErrorJson.decodeFromString(bodyText)
}

/** True, pokud chyba je HTTP 401 (vypršelý/neplatný token) -> volající má odhlásit. */
fun Throwable.isUnauthorized(): Boolean =
    (this as? ResponseException)?.response?.status?.value == 401

@Serializable private data class ApiErrorBody(val message: String? = null)

/** Extracts the API's actual error message instead of Ktor's verbose default. */
suspend fun Throwable.apiMessage(): String {
    val bodyText = (this as? ResponseException)?.response?.bodyAsText() ?: return message ?: "neznámá chyba"
    return try {
        apiErrorJson.decodeFromString<ApiErrorBody>(bodyText).message ?: bodyText
    } catch (e: Throwable) {
        bodyText
    }
}

// ---------------------------------------------------------------------------------------------
// bowlingliga team API — contract DTOs (see docs/IMPLEMENTATION_PLAN.md §1). These endpoints are
// built in the bowlingliga repo in phase 1; the fetch helpers below are ready to call them.
// ---------------------------------------------------------------------------------------------

@Serializable
data class TeamSummaryDto(
    val standing: TeamStandingDto? = null,
    val lastMatches: List<TeamMatchDto> = emptyList(),
    val nextMatch: TeamMatchDto? = null,
    val overdueMatches: List<TeamMatchDto> = emptyList(),
)

@Serializable
data class TeamStandingDto(
    val leagueId: String,
    val leagueName: String,
    val position: Int? = null,
    val points: Int = 0,
    val played: Int = 0,
)

@Serializable
data class TeamMatchesResponseDto(val matches: List<TeamMatchDto> = emptyList())

// bowlingliga raw duely/lineup (veřejné tabulky) — pro kompletní zápis k ligovým hrám
@Serializable
data class BlLineupDto(val position: String, @SerialName("player_id") val playerId: String)

@Serializable
private data class BlMatchDateDto(val id: String, @SerialName("played_at") val playedAt: String? = null)

@Serializable
private data class BlRoundSlotDto(
    @SerialName("round_number") val roundNumber: Int,
    @SerialName("date_from") val dateFrom: String,
    @SerialName("date_to") val dateTo: String? = null,
)

@Serializable
data class BlDuelDto(
    val sequence: Int,
    @SerialName("home_position") val homePosition: String,
    @SerialName("away_position") val awayPosition: String,
    @SerialName("home_score") val homeScore: Int? = null,
    @SerialName("away_score") val awayScore: Int? = null,
)

@Serializable
data class TeamMatchDto(
    val id: String? = null,
    val date: String? = null,
    val opponent: String,
    val home: Boolean,
    val status: String? = null,
    val result: String? = null,          // "win" | "loss" | "draw" (z pohledu týmu)
    val teamScore: Int? = null,          // celkový nához
    val oppScore: Int? = null,
    val teamDuelPoints: Int? = null,     // duelové body 0-9 (rozhodují výsledek)
    val oppDuelPoints: Int? = null,
    val roundNumber: Int? = null,
    val phase: String? = null,
    val roundDateFrom: String? = null,
    val roundDateTo: String? = null,
)

@Serializable
data class TokenResponse(
    @SerialName("access_token") val accessToken: String? = null,
    @SerialName("error_description") val errorDescription: String? = null,
)

// ---- team-profile ----

@Serializable
data class TeamProfileDto(
    val team: TeamInfoDto,
    val season: SeasonRefDto? = null,
    val leagues: List<LeagueRefDto> = emptyList(),
    val roster: List<RosterPlayerDto> = emptyList(),
)

@Serializable
data class TeamInfoDto(val id: String, val name: String, val displayName: String, val averageScore: Double? = null)

@Serializable
data class SeasonRefDto(val id: String, val name: String)

@Serializable
data class LeagueRefDto(val leagueId: String, val leagueName: String, val seed: Int? = null)

@Serializable
data class RosterPlayerDto(val playerId: String, val name: String, val fullName: String? = null, val gender: String? = null)

// ---- team-player-games (per-hra ligová skóre pro statistiky) ----

@Serializable
data class TeamPlayerGamesResponseDto(val players: List<PlayerGamesDto> = emptyList())

@Serializable
data class PlayerGamesDto(
    val playerId: String,
    val name: String,
    val fullName: String? = null,
    val games: List<LeagueGameRecDto> = emptyList(),
)

@Serializable
data class LeagueGameRecDto(
    val score: Int,
    val date: String? = null,
    val seasonId: String,
    val seasonName: String,
    val roundNumber: Int,
    val phase: String,
    val matchId: String,
    val gameNo: Int,
)

// Zápis hráče napříč zdroji (pro statistiky hráče: seznam her + editace zápisu).
@Serializable
data class PlayerSheetDto(
    @SerialName("source_kind") val sourceKind: String,
    @SerialName("bl_player_id") val blPlayerId: String = "",
    @SerialName("training_id") val trainingId: String? = null,
    @SerialName("tournament_id") val tournamentId: String? = null,
    @SerialName("bl_match_id") val blMatchId: String? = null,
    @SerialName("game_no") val gameNo: Int,
    @SerialName("game_type") val gameType: String = "classic",
    val frames: List<FrameDto> = emptyList(),
    val total: Int,
) {
    /** Klíč hry: "source:refId:gameNo" (refId = training/tournament/match id). */
    fun key(): String {
        val ref = trainingId ?: tournamentId ?: blMatchId ?: ""
        return "$sourceKind:$ref:$gameNo"
    }
}

// ---- team-player-history ----

@Serializable
data class PlayerHistoryResponseDto(val players: List<PlayerHistoryDto> = emptyList())

@Serializable
data class PlayerHistoryDto(
    val playerId: String,
    val name: String,
    val fullName: String? = null,
    val seasons: List<PlayerSeasonStatsDto> = emptyList(),
)

@Serializable
data class PlayerSeasonStatsDto(
    val seasonId: String,
    val seasonName: String,
    val games: Int,
    val average: Double,
    val minScore: Int,
    val maxScore: Int,
)

// ---- sgbowling vlastní data (členská sekce, fáze 3) ----

@Serializable
data class SgMemberDto(
    @SerialName("user_id") val userId: String,
    @SerialName("bl_player_id") val blPlayerId: String? = null,
    val role: String,
)

@Serializable
data class SgProfileDto(
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("display_name") val displayName: String,
    val nickname: String? = null,
    @SerialName("photo_url") val photoUrl: String? = null,
    @SerialName("bio_html") val bioHtml: String = "",
    @SerialName("is_captain") val isCaptain: Boolean = false,
    @SerialName("is_active") val isActive: Boolean = true,
    @SerialName("sort_order") val sortOrder: Int = 0,
)

@Serializable
private data class ProfileUpsertInput(
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("display_name") val displayName: String,
    val nickname: String? = null,
    @SerialName("photo_url") val photoUrl: String? = null,
    @SerialName("bio_html") val bioHtml: String,
    @SerialName("is_captain") val isCaptain: Boolean,
    @SerialName("is_active") val isActive: Boolean,
    @SerialName("sort_order") val sortOrder: Int,
)

@Serializable
data class SgTrainingDto(
    val id: String,
    val date: String,
    val location: String? = null,
    val notes: String? = null,
)

@Serializable
data class SgScoreDto(
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("game_no") val gameNo: Int,
    val score: Int,
    @SerialName("game_type") val gameType: String = "classic",
    @SerialName("training_id") val trainingId: String? = null,
    @SerialName("tournament_id") val tournamentId: String? = null,
)

@Serializable
private data class CreateTrainingInput(
    val date: String,
    val location: String? = null,
    val notes: String? = null,
    @SerialName("created_by") val createdBy: String,
)

@Serializable
private data class TrainingScoreInput(
    @SerialName("training_id") val trainingId: String,
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("game_no") val gameNo: Int,
    val score: Int,
    @SerialName("game_type") val gameType: String,
)

/** UI model pro zadání skóre (není serializován přímo — mapuje se na *ScoreInput při zápisu). */
data class ScoreEntry(val blPlayerId: String, val gameNo: Int, val score: Int, val gameType: String = "classic")

// ---- kompletní zápis (per hod/frame) ----

@Serializable
data class FrameDto(val t: List<Int> = emptyList(), val split: Boolean = false)

@Serializable
data class SgScoresheetDto(
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("game_no") val gameNo: Int,
    @SerialName("game_type") val gameType: String = "classic",
    val frames: List<FrameDto> = emptyList(),
    val total: Int,
    @SerialName("training_id") val trainingId: String? = null,
    @SerialName("tournament_id") val tournamentId: String? = null,
)

// ---- turnaje ----

@Serializable
data class SgTournamentDto(
    val id: String,
    val name: String,
    val date: String,
    val location: String? = null,
    val notes: String? = null,
    @SerialName("game_type") val gameType: String = "classic",
)

@Serializable
data class SgPlacementDto(
    @SerialName("bl_player_id") val blPlayerId: String,
    val placement: Int,
    @SerialName("tournament_id") val tournamentId: String? = null,
)

@Serializable
private data class CreateTournamentInput(
    val name: String,
    val date: String,
    @SerialName("game_type") val gameType: String,
    val location: String? = null,
    val notes: String? = null,
    @SerialName("created_by") val createdBy: String,
)

@Serializable
private data class TournamentScoreInput(
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("game_no") val gameNo: Int,
    val score: Int,
    @SerialName("game_type") val gameType: String,
)

@Serializable
private data class TournamentSheetInput(
    @SerialName("source_kind") val sourceKind: String,
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("game_no") val gameNo: Int,
    @SerialName("game_type") val gameType: String,
    val frames: List<FrameDto>,
    val total: Int,
    @SerialName("created_by") val createdBy: String,
)

@Serializable
private data class PlacementInput(
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("bl_player_id") val blPlayerId: String,
    val placement: Int,
)

/** UI model umístění hráče. */
data class PlacementEntry(val blPlayerId: String, val placement: Int)

@Serializable
private data class TrainingSheetInput(
    @SerialName("source_kind") val sourceKind: String,
    @SerialName("training_id") val trainingId: String,
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("game_no") val gameNo: Int,
    @SerialName("game_type") val gameType: String,
    val frames: List<FrameDto>,
    val total: Int,
    @SerialName("created_by") val createdBy: String,
)

/** UI model jednoho zápisu k uložení. */
data class SheetEntry(val blPlayerId: String, val gameNo: Int, val gameType: String, val frames: List<FrameDto>, val total: Int)

@Serializable
private data class LeagueSheetInput(
    @SerialName("source_kind") val sourceKind: String,
    @SerialName("bl_match_id") val blMatchId: String,
    @SerialName("bl_player_id") val blPlayerId: String,
    @SerialName("game_no") val gameNo: Int,
    @SerialName("game_type") val gameType: String,
    val frames: List<FrameDto>,
    val total: Int,
    @SerialName("created_by") val createdBy: String,
)

/** UI model ligového zápisu k uložení (typ vždy classic). */
data class LeagueGameSheet(val blPlayerId: String, val gameNo: Int, val frames: List<FrameDto>, val total: Int)

// ---- CMS (fáze 5): úvodní text na Home + aktuality ----

@Serializable
data class SgContentDto(val key: String, val value: String = "")

@Serializable
data class SgNewsDto(
    val id: String,
    val title: String,
    val body: String = "",
    val published: Boolean = true,
    @SerialName("created_at") val createdAt: String? = null,
)

@Serializable
private data class ContentUpsertInput(val key: String, val value: String)

@Serializable
private data class NewsInput(val title: String, val body: String, val published: Boolean)

// ---- dětská sekce: vlastní identita (sg_child.id), žádná vazba na bl_player_id ----
// Struktura zrcadlí dospělé (trénink/turnaj), navíc "kolo ligy" (sg_child_round) - děti hrají
// vlastní ligu bez přímého soupeře, jen s pořadím za kolo mezi našimi hráči. Vlastní typy her
// (classic/mantinely/dvojice) místo dospělého classic/nine/eight/narubak.

@Serializable
data class SgChildDto(
    val id: String,
    @SerialName("display_name") val displayName: String,
    val nickname: String? = null,
    @SerialName("photo_url") val photoUrl: String? = null,
    @SerialName("bio_html") val bioHtml: String = "",
    @SerialName("is_active") val isActive: Boolean = true,
    @SerialName("sort_order") val sortOrder: Int = 0,
)

@Serializable
private data class ChildUpsertInput(
    val id: String? = null,
    @SerialName("display_name") val displayName: String,
    val nickname: String? = null,
    @SerialName("photo_url") val photoUrl: String? = null,
    @SerialName("bio_html") val bioHtml: String,
    @SerialName("is_active") val isActive: Boolean,
    @SerialName("sort_order") val sortOrder: Int,
)

@Serializable
data class SgChildTrainingDto(val id: String, val date: String, val location: String? = null, val notes: String? = null)

@Serializable
data class SgChildRoundDto(
    val id: String,
    val date: String,
    @SerialName("round_no") val roundNo: Int? = null,
    val location: String? = null,
    val notes: String? = null,
)

@Serializable
data class SgChildTournamentDto(
    val id: String,
    val name: String,
    val date: String,
    val location: String? = null,
    val notes: String? = null,
    @SerialName("game_type") val gameType: String = "classic",
)

@Serializable
data class SgChildScoreDto(
    @SerialName("child_id") val childId: String,
    @SerialName("game_no") val gameNo: Int,
    val score: Int,
    @SerialName("game_type") val gameType: String = "classic",
    @SerialName("training_id") val trainingId: String? = null,
    @SerialName("round_id") val roundId: String? = null,
    @SerialName("tournament_id") val tournamentId: String? = null,
)

@Serializable
data class SgChildRoundPlacementDto(
    @SerialName("child_id") val childId: String,
    val placement: Int,
    @SerialName("round_id") val roundId: String? = null,
)

@Serializable
data class SgChildTournamentEntryDto(
    @SerialName("child_id") val childId: String,
    val placement: Int? = null,
    @SerialName("partner_note") val partnerNote: String? = null,
    @SerialName("tournament_id") val tournamentId: String? = null,
)

@Serializable
data class SgChildScoresheetDto(
    @SerialName("child_id") val childId: String,
    @SerialName("game_no") val gameNo: Int,
    @SerialName("game_type") val gameType: String = "classic",
    val frames: List<FrameDto> = emptyList(),
    val total: Int,
    @SerialName("training_id") val trainingId: String? = null,
    @SerialName("round_id") val roundId: String? = null,
    @SerialName("tournament_id") val tournamentId: String? = null,
)

/** UI modely pro zadání skóre/umístění dítěte (nejsou serializovány přímo). */
data class ChildScoreEntry(val childId: String, val gameNo: Int, val score: Int, val gameType: String = "classic")
data class ChildPlacementEntry(val childId: String, val placement: Int)
data class ChildTournamentEntryEntry(val childId: String, val placement: Int?, val partnerNote: String?)
data class ChildSheetEntry(val childId: String, val gameNo: Int, val gameType: String, val frames: List<FrameDto>, val total: Int)

@Serializable private data class CreateChildTrainingInput(val date: String, val location: String? = null, val notes: String? = null, @SerialName("created_by") val createdBy: String)
@Serializable private data class ChildTrainingScoreInput(@SerialName("training_id") val trainingId: String, @SerialName("child_id") val childId: String, @SerialName("game_no") val gameNo: Int, val score: Int, @SerialName("game_type") val gameType: String)
@Serializable private data class ChildTrainingSheetInput(@SerialName("source_kind") val sourceKind: String, @SerialName("training_id") val trainingId: String, @SerialName("child_id") val childId: String, @SerialName("game_no") val gameNo: Int, @SerialName("game_type") val gameType: String, val frames: List<FrameDto>, val total: Int, @SerialName("created_by") val createdBy: String)

@Serializable private data class CreateChildRoundInput(val date: String, @SerialName("round_no") val roundNo: Int?, val location: String? = null, val notes: String? = null, @SerialName("created_by") val createdBy: String)
@Serializable private data class UpdateChildRoundInput(val date: String, @SerialName("round_no") val roundNo: Int?, val notes: String?)
@Serializable private data class ChildRoundScoreInput(@SerialName("round_id") val roundId: String, @SerialName("child_id") val childId: String, @SerialName("game_no") val gameNo: Int, val score: Int, @SerialName("game_type") val gameType: String)
@Serializable private data class ChildRoundPlacementInput(@SerialName("round_id") val roundId: String, @SerialName("child_id") val childId: String, val placement: Int)
@Serializable private data class ChildRoundSheetInput(@SerialName("source_kind") val sourceKind: String, @SerialName("round_id") val roundId: String, @SerialName("child_id") val childId: String, @SerialName("game_no") val gameNo: Int, @SerialName("game_type") val gameType: String, val frames: List<FrameDto>, val total: Int, @SerialName("created_by") val createdBy: String)

@Serializable private data class CreateChildTournamentInput(val name: String, val date: String, @SerialName("game_type") val gameType: String, val location: String? = null, val notes: String? = null, @SerialName("created_by") val createdBy: String)
@Serializable private data class ChildTournamentScoreInput(@SerialName("tournament_id") val tournamentId: String, @SerialName("child_id") val childId: String, @SerialName("game_no") val gameNo: Int, val score: Int, @SerialName("game_type") val gameType: String)
@Serializable private data class ChildTournamentEntryInput(@SerialName("tournament_id") val tournamentId: String, @SerialName("child_id") val childId: String, val placement: Int?, @SerialName("partner_note") val partnerNote: String?)
@Serializable private data class ChildTournamentSheetInput(@SerialName("source_kind") val sourceKind: String, @SerialName("tournament_id") val tournamentId: String, @SerialName("child_id") val childId: String, @SerialName("game_no") val gameNo: Int, @SerialName("game_type") val gameType: String, val frames: List<FrameDto>, val total: Int, @SerialName("created_by") val createdBy: String)

/** Auth for bowlingliga's Supabase gateway: its edge functions require bowlingliga's OWN anon key
 *  (BL_ANON_KEY), not sgbowling's -- they live on a different Supabase project. Read-only, public. */
private fun HttpRequestBuilder.blAuth() {
    header("apikey", BL_ANON_KEY)
    header(HttpHeaders.Authorization, "Bearer $BL_ANON_KEY")
}

/** bowlingliga REST base (veřejné tabulky match_lineup/match_duel) — odvozeno z BL_API_BASE. */
private val BL_REST_BASE = BL_API_BASE.removeSuffix("/functions/v1") + "/rest/v1"

/** Auth for sgbowling's OWN Supabase (members/trainings/tournaments/profiles). Anon when no token. */
private fun HttpRequestBuilder.sgAuth(token: String?) {
    header("apikey", SG_ANON_KEY)
    header(HttpHeaders.Authorization, "Bearer ${token ?: SG_ANON_KEY}")
}

object Api {
    /** Skutečné datum konkrétního zápasu. Agregované team endpointy zatím vracejí datum kola. */
    private suspend fun fetchBlMatchPlayedDates(matchIds: Collection<String>): Map<String, String> {
        val result = mutableMapOf<String, String>()
        matchIds.filter { it.isNotBlank() }.distinct().chunked(80).forEach { ids ->
            val rows: List<BlMatchDateDto> = httpClient.get("$BL_REST_BASE/match") {
                blAuth()
                parameter("select", "id,played_at")
                parameter("id", "in.(${ids.joinToString(",")})")
            }.body()
            rows.forEach { row ->
                row.playedAt?.takeIf { it.isNotBlank() }?.let { result[row.id] = it.take(10) }
            }
        }
        return result
    }

    private fun TeamMatchDto.withPlayedDate(dates: Map<String, String>): TeamMatchDto =
        id?.let(dates::get)?.let { copy(date = it) } ?: this

    /** GET bowlingliga team-summary for Sunny Gang (last matches, next match, standing). */
    suspend fun fetchTeamSummary(): TeamSummaryDto = singleFlight("team-summary:$SG_TEAM_ID") {
        val (summary, profile) = coroutineScope {
            val summaryRequest = async {
                conditionalGet<TeamSummaryDto>("team-summary:$SG_TEAM_ID", "$BL_API_BASE/team-summary?teamId=$SG_TEAM_ID") { blAuth() }
            }
            val profileRequest = async { fetchTeamProfile() }
            summaryRequest.await() to profileRequest.await()
        }
        val (matches, slots) = coroutineScope {
            val matchRequest = async { fetchTeamMatches(profile.season?.id) }
            val slotRequest = async {
                profile.season?.id?.let { seasonId ->
                    httpClient.get("$BL_REST_BASE/season_round_slot") {
                        blAuth()
                        parameter("select", "round_number,date_from,date_to")
                        parameter("season_id", "eq.$seasonId")
                    }.body<List<BlRoundSlotDto>>()
                } ?: emptyList()
            }
            matchRequest.await() to slotRequest.await()
        }
        val slotByRound = slots.associateBy { it.roundNumber }
        val datedMatches = matches.map { match ->
            val slot = slotByRound[match.roundNumber]
            match.copy(roundDateFrom = slot?.dateFrom, roundDateTo = slot?.dateTo)
        }
        val schedule = teamSchedule(datedMatches, leagueDateIso())
        val matchById = datedMatches.filter { it.id != null }.associateBy { it.id }
        summary.copy(
            lastMatches = summary.lastMatches.map { matchById[it.id] ?: it },
            nextMatch = schedule.next,
            overdueMatches = schedule.overdue,
        )
    }

    /** GET bowlingliga team-profile (team, season, leagues, roster with full names). */
    suspend fun fetchTeamProfile(): TeamProfileDto = singleFlight("team-profile:$SG_TEAM_ID") {
        conditionalGet("team-profile:$SG_TEAM_ID", "$BL_API_BASE/team-profile?teamId=$SG_TEAM_ID") {
            blAuth()
        }
    }

    /** GET bowlingliga team-player-history (per-player league performance broken down by season). */
    suspend fun fetchTeamPlayerHistory(): PlayerHistoryResponseDto = singleFlight("team-player-history:$SG_TEAM_ID") {
        conditionalGet("team-player-history:$SG_TEAM_ID", "$BL_API_BASE/team-player-history?teamId=$SG_TEAM_ID") {
            blAuth()
        }
    }

    /** GET bowlingliga team-player-games (per-hra ligová skóre, pro kombinované statistiky). */
    suspend fun fetchTeamPlayerGames(): List<PlayerGamesDto> = singleFlight("team-player-games:$SG_TEAM_ID") {
        val players = conditionalGet<TeamPlayerGamesResponseDto>("team-player-games:$SG_TEAM_ID", "$BL_API_BASE/team-player-games?teamId=$SG_TEAM_ID") {
            blAuth()
        }.players
        val dates = fetchBlMatchPlayedDates(players.flatMap { player -> player.games.map { it.matchId } })
        players.map { player ->
            player.copy(games = player.games.map { game -> dates[game.matchId]?.let { game.copy(date = it) } ?: game })
        }
    }

    // ---- sgbowling členská sekce (vlastní Supabase) ----

    /** Přihlášení heslem proti sgbowling Supabase Auth. */
    suspend fun login(email: String, password: String): TokenResponse =
        httpClient.post("$SG_SUPABASE_URL/auth/v1/token?grant_type=password") {
            expectSuccess = false
            header("apikey", SG_ANON_KEY)
            contentType(ContentType.Application.Json)
            setBody(mapOf("email" to email, "password" to password))
        }.body()

    /** Nastaví nové heslo uživatele. accessToken = recovery token z e-mailového odkazu (nebo session). */
    suspend fun updatePassword(accessToken: String, newPassword: String) {
        httpClient.put("$SG_SUPABASE_URL/auth/v1/user") {
            header("apikey", SG_ANON_KEY)
            header(HttpHeaders.Authorization, "Bearer $accessToken")
            contentType(ContentType.Application.Json)
            setBody(mapOf("password" to newPassword))
        }
    }

    /** Účet přihlášeného člena (role, mapování na hráče). RLS vrací jen vlastní řádek. */
    suspend fun fetchMyMember(token: String): SgMemberDto? =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_member") {
            sgAuth(token)
            parameter("select", "user_id,bl_player_id,role")
        }.body<List<SgMemberDto>>().firstOrNull()

    /** Veřejné profily (medailonky), řazené dle sort_order. */
    suspend fun fetchSgProfiles(): List<SgProfileDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_profile") {
            sgAuth(null)
            parameter("select", "bl_player_id,display_name,nickname,photo_url,bio_html,is_captain,is_active,sort_order")
            parameter("order", "sort_order")
        }.body()

    suspend fun fetchTrainings(token: String): List<SgTrainingDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_training") {
            sgAuth(token)
            parameter("select", "id,date,location,notes")
            parameter("order", "date.desc")
        }.body()

    suspend fun fetchTrainingScores(token: String, trainingIds: List<String>): List<SgScoreDto> {
        if (trainingIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_training_score") {
            sgAuth(token)
            parameter("select", "training_id,bl_player_id,game_no,score,game_type")
            parameter("training_id", "in.(${trainingIds.joinToString(",")})")
        }.body()
    }

    /** Vytvoří trénink a vrátí jeho id. */
    suspend fun createTraining(token: String, createdBy: String, date: String, location: String?, notes: String?): String =
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_training") {
            sgAuth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(listOf(CreateTrainingInput(date, location?.ifBlank { null }, notes?.ifBlank { null }, createdBy)))
        }.body<List<SgTrainingDto>>().first().id

    suspend fun addTrainingScores(token: String, trainingId: String, entries: List<ScoreEntry>) {
        if (entries.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_training_score") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(entries.map { TrainingScoreInput(trainingId, it.blPlayerId, it.gameNo, it.score, it.gameType) })
        }
    }

    suspend fun updateTraining(token: String, id: String, date: String, notes: String?) {
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_training?id=eq.$id") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("date" to date, "notes" to notes?.ifBlank { null }))
        }
    }

    suspend fun deleteTrainingScores(token: String, trainingId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_training_score?training_id=eq.$trainingId") { sgAuth(token) }
    }

    /** Smaže trénink (skóre i zápisy padnou s ním díky on delete cascade). */
    suspend fun deleteTraining(token: String, id: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_training?id=eq.$id") { sgAuth(token) }
    }

    // ---- kompletní zápisy tréninku ----

    suspend fun fetchTrainingSheets(token: String, trainingIds: List<String>): List<SgScoresheetDto> {
        if (trainingIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            parameter("select", "training_id,bl_player_id,game_no,game_type,frames,total")
            parameter("training_id", "in.(${trainingIds.joinToString(",")})")
        }.body()
    }

    suspend fun addTrainingSheets(token: String, trainingId: String, createdBy: String, sheets: List<SheetEntry>) {
        if (sheets.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(sheets.map {
                TrainingSheetInput(
                    sourceKind = "training", trainingId = trainingId, blPlayerId = it.blPlayerId, gameNo = it.gameNo,
                    gameType = it.gameType, frames = it.frames, total = it.total, createdBy = createdBy,
                )
            })
        }
    }

    suspend fun deleteTrainingSheets(token: String, trainingId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_scoresheet?training_id=eq.$trainingId") { sgAuth(token) }
    }

    // ---- turnaje ----

    suspend fun fetchTournaments(token: String): List<SgTournamentDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_tournament") {
            sgAuth(token)
            parameter("select", "id,name,date,location,notes,game_type")
            parameter("order", "date.desc")
        }.body()

    suspend fun createTournament(token: String, createdBy: String, name: String, date: String, gameType: String, notes: String?): String =
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_tournament") {
            sgAuth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(listOf(CreateTournamentInput(name, date, gameType, null, notes?.ifBlank { null }, createdBy)))
        }.body<List<SgTournamentDto>>().first().id

    suspend fun updateTournament(token: String, id: String, name: String, date: String, gameType: String, notes: String?) {
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_tournament?id=eq.$id") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("name" to name, "date" to date, "game_type" to gameType, "notes" to notes?.ifBlank { null }))
        }
    }

    suspend fun deleteTournament(token: String, id: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_tournament?id=eq.$id") { sgAuth(token) }
    }

    suspend fun fetchTournamentScores(token: String, tournamentIds: List<String>): List<SgScoreDto> {
        if (tournamentIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_tournament_score") {
            sgAuth(token)
            parameter("select", "tournament_id,bl_player_id,game_no,score,game_type")
            parameter("tournament_id", "in.(${tournamentIds.joinToString(",")})")
        }.body()
    }

    suspend fun addTournamentScores(token: String, tournamentId: String, entries: List<ScoreEntry>) {
        if (entries.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_tournament_score") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(entries.map { TournamentScoreInput(tournamentId, it.blPlayerId, it.gameNo, it.score, it.gameType) })
        }
    }

    suspend fun deleteTournamentScores(token: String, tournamentId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_tournament_score?tournament_id=eq.$tournamentId") { sgAuth(token) }
    }

    suspend fun fetchTournamentSheets(token: String, tournamentIds: List<String>): List<SgScoresheetDto> {
        if (tournamentIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            parameter("select", "tournament_id,bl_player_id,game_no,game_type,frames,total")
            parameter("tournament_id", "in.(${tournamentIds.joinToString(",")})")
        }.body()
    }

    suspend fun addTournamentSheets(token: String, tournamentId: String, createdBy: String, sheets: List<SheetEntry>) {
        if (sheets.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(sheets.map {
                TournamentSheetInput(
                    sourceKind = "tournament", tournamentId = tournamentId, blPlayerId = it.blPlayerId, gameNo = it.gameNo,
                    gameType = it.gameType, frames = it.frames, total = it.total, createdBy = createdBy,
                )
            })
        }
    }

    suspend fun deleteTournamentSheets(token: String, tournamentId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_scoresheet?tournament_id=eq.$tournamentId") { sgAuth(token) }
    }

    suspend fun fetchTournamentPlacements(token: String, tournamentIds: List<String>): List<SgPlacementDto> {
        if (tournamentIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_tournament_placement") {
            sgAuth(token)
            parameter("select", "tournament_id,bl_player_id,placement")
            parameter("tournament_id", "in.(${tournamentIds.joinToString(",")})")
        }.body()
    }

    suspend fun addTournamentPlacements(token: String, tournamentId: String, placements: List<PlacementEntry>) {
        if (placements.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_tournament_placement") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(placements.map { PlacementInput(tournamentId, it.blPlayerId, it.placement) })
        }
    }

    suspend fun deleteTournamentPlacements(token: String, tournamentId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_tournament_placement?tournament_id=eq.$tournamentId") { sgAuth(token) }
    }

    // ---- veřejné turnajové výsledky (medailonky / detail hráče, čte se anonymně) ----

    suspend fun fetchPublicTournaments(): List<SgTournamentDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_tournament") {
            sgAuth(null)
            parameter("select", "id,name,date,game_type")
            parameter("order", "date.desc")
        }.body()

    suspend fun fetchPlayerTournamentScores(blPlayerId: String): List<SgScoreDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_tournament_score") {
            sgAuth(null)
            parameter("select", "tournament_id,bl_player_id,game_no,score,game_type")
            parameter("bl_player_id", "eq.$blPlayerId")
        }.body()

    suspend fun fetchPlayerPlacements(blPlayerId: String): List<SgPlacementDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_tournament_placement") {
            sgAuth(null)
            parameter("select", "tournament_id,bl_player_id,placement")
            parameter("bl_player_id", "eq.$blPlayerId")
        }.body()

    // ---- ligové zápasy (bowlingliga) + kompletní zápis k nim ----

    /** Zápasy týmu v sezóně (default aktivní) z bowlingliga team-matches. */
    suspend fun fetchTeamMatches(seasonId: String?): List<TeamMatchDto> {
        val q = "$BL_API_BASE/team-matches?teamId=$SG_TEAM_ID" + (seasonId?.let { "&seasonId=$it" } ?: "")
        return singleFlight("team-matches:$seasonId") {
            val matches = conditionalGet<TeamMatchesResponseDto>("team-matches:$seasonId", q) { blAuth() }.matches
            val dates = fetchBlMatchPlayedDates(matches.mapNotNull { it.id })
            matches.map { it.withPlayedDate(dates) }
        }
    }

    /** Sestava týmu v zápase (pozice -> hráč). Veřejná bowlingliga tabulka. */
    suspend fun fetchBlMatchLineup(matchId: String): List<BlLineupDto> =
        httpClient.get("$BL_REST_BASE/match_lineup") {
            blAuth()
            parameter("select", "position,player_id")
            parameter("match_id", "eq.$matchId")
            parameter("team_id", "eq.$SG_TEAM_ID")
        }.body()

    /** Duely zápasu (9 her) z bowlingligy. */
    suspend fun fetchBlMatchDuels(matchId: String): List<BlDuelDto> =
        httpClient.get("$BL_REST_BASE/match_duel") {
            blAuth()
            parameter("select", "sequence,home_position,away_position,home_score,away_score")
            parameter("match_id", "eq.$matchId")
            parameter("order", "sequence")
        }.body()

    /** Existující kompletní zápisy sgbowling k ligovému zápasu. */
    suspend fun fetchLeagueSheets(token: String, matchId: String): List<SgScoresheetDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            parameter("select", "bl_player_id,game_no,game_type,frames,total")
            parameter("bl_match_id", "eq.$matchId")
        }.body()

    suspend fun addLeagueSheets(token: String, matchId: String, createdBy: String, sheets: List<LeagueGameSheet>) {
        if (sheets.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(sheets.map { LeagueSheetInput(sourceKind = "league", blMatchId = matchId, blPlayerId = it.blPlayerId, gameNo = it.gameNo, gameType = "classic", frames = it.frames, total = it.total, createdBy = createdBy) })
        }
    }

    suspend fun deleteLeagueSheets(token: String, matchId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_scoresheet?bl_match_id=eq.$matchId") { sgAuth(token) }
    }

    // ---- zápisy jednoho hráče napříč zdroji (statistiky hráče) ----

    suspend fun fetchPlayerSheets(token: String, blPlayerId: String): List<PlayerSheetDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            parameter("select", "source_kind,bl_player_id,training_id,tournament_id,bl_match_id,game_no,game_type,frames,total")
            parameter("bl_player_id", "eq.$blPlayerId")
        }.body()

    /** Kompletní zápisy všech hráčů pro týmové frameové statistiky. */
    suspend fun fetchTeamSheets(token: String): List<PlayerSheetDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_scoresheet") {
            sgAuth(token)
            parameter("select", "source_kind,bl_player_id,training_id,tournament_id,bl_match_id,game_no,game_type,frames,total")
        }.body()

    private fun refColumn(sourceKind: String) = when (sourceKind) {
        "training" -> "training_id"
        "tournament" -> "tournament_id"
        else -> "bl_match_id"
    }

    /** Smaže konkrétní zápis (jedna hra) dle zdroje + hráče + čísla hry. */
    suspend fun deleteSheet(token: String, sourceKind: String, refId: String, blPlayerId: String, gameNo: Int) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_scoresheet?${refColumn(sourceKind)}=eq.$refId&bl_player_id=eq.$blPlayerId&game_no=eq.$gameNo") { sgAuth(token) }
    }

    /** Uloží (nahradí) zápis jedné hry. */
    suspend fun saveOneSheet(
        token: String, sourceKind: String, refId: String, blPlayerId: String, gameNo: Int,
        gameType: String, frames: List<FrameDto>, total: Int, createdBy: String,
    ) {
        deleteSheet(token, sourceKind, refId, blPlayerId, gameNo)
        val url = "$SG_SUPABASE_URL/rest/v1/sg_scoresheet"
        when (sourceKind) {
            "training" -> httpClient.post(url) {
                sgAuth(token); contentType(ContentType.Application.Json)
                setBody(listOf(TrainingSheetInput("training", refId, blPlayerId, gameNo, gameType, frames, total, createdBy)))
            }
            "tournament" -> httpClient.post(url) {
                sgAuth(token); contentType(ContentType.Application.Json)
                setBody(listOf(TournamentSheetInput("tournament", refId, blPlayerId, gameNo, gameType, frames, total, createdBy)))
            }
            else -> httpClient.post(url) {
                sgAuth(token); contentType(ContentType.Application.Json)
                setBody(listOf(LeagueSheetInput("league", refId, blPlayerId, gameNo, gameType, frames, total, createdBy)))
            }
        }
    }

    // ---- CMS: úvodní text (Home) + aktuality ----

    /** Úvodní text na Home. Veřejné čtení (anon), null token = anon. */
    suspend fun fetchContent(key: String): String? =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_content") {
            sgAuth(null)
            parameter("select", "key,value")
            parameter("key", "eq.$key")
        }.body<List<SgContentDto>>().firstOrNull()?.value

    /** Upsert obsahu (admin). Klíč je primary key -> merge při konfliktu. */
    suspend fun saveContent(token: String, key: String, value: String) {
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_content") {
            sgAuth(token)
            header("Prefer", "resolution=merge-duplicates")
            contentType(ContentType.Application.Json)
            setBody(listOf(ContentUpsertInput(key, value)))
        }
    }

    /** Aktuality. Anon vidí jen publikované; admin token vrací i nepublikované (RLS). */
    suspend fun fetchNews(token: String?): List<SgNewsDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_news") {
            sgAuth(token)
            parameter("select", "id,title,body,published,created_at")
            parameter("order", "created_at.desc")
        }.body()

    suspend fun createNews(token: String, title: String, body: String, published: Boolean) {
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_news") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(listOf(NewsInput(title, body, published)))
        }
    }

    suspend fun updateNews(token: String, id: String, title: String, body: String, published: Boolean) {
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_news?id=eq.$id") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(NewsInput(title, body, published))
        }
    }

    suspend fun deleteNews(token: String, id: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_news?id=eq.$id") { sgAuth(token) }
    }

    // ---- admin: správa členů a profilů ----

    /** Všechny účty členů (jen admin dle RLS). */
    suspend fun fetchAllMembers(token: String): List<SgMemberDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_member") {
            sgAuth(token)
            parameter("select", "user_id,bl_player_id,role")
        }.body()

    /** Změna role / mapování člena na hráče (admin). */
    suspend fun updateMember(token: String, userId: String, role: String, blPlayerId: String?) {
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_member?user_id=eq.$userId") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("role" to role, "bl_player_id" to blPlayerId))
        }
    }

    /** Všechny profily (i neaktivní) pro admin editaci. */
    suspend fun fetchProfilesAdmin(token: String): List<SgProfileDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_profile") {
            sgAuth(token)
            parameter("select", "bl_player_id,display_name,nickname,photo_url,bio_html,is_captain,is_active,sort_order")
            parameter("order", "sort_order")
        }.body()

    /** Upsert medailonku (admin). Klíč bl_player_id -> merge při konfliktu. */
    suspend fun upsertProfile(
        token: String, blPlayerId: String, displayName: String, nickname: String?,
        photoUrl: String?, bioHtml: String, isCaptain: Boolean, isActive: Boolean, sortOrder: Int,
    ) {
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_profile") {
            sgAuth(token)
            header("Prefer", "resolution=merge-duplicates")
            contentType(ContentType.Application.Json)
            setBody(listOf(ProfileUpsertInput(blPlayerId, displayName, nickname?.ifBlank { null }, photoUrl?.ifBlank { null }, bioHtml, isCaptain, isActive, sortOrder)))
        }
    }

    /** Nahraje soubor do bucketu sg-media a vrátí veřejnou URL. */
    suspend fun uploadMedia(token: String, path: String, contentType: String, bytes: ByteArray): String {
        httpClient.post("$SG_SUPABASE_URL/storage/v1/object/sg-media/$path") {
            header("apikey", SG_ANON_KEY)
            header(HttpHeaders.Authorization, "Bearer $token")
            header("x-upsert", "true")
            contentType(ContentType.parse(contentType))
            setBody(bytes)
        }
        return "$SG_SUPABASE_URL/storage/v1/object/public/sg-media/$path"
    }

    // ============================================================================================
    // Dětská sekce - vlastní identita (sg_child), žádná vazba na bowlingligu.
    // ============================================================================================

    // ---- roster (veřejné čtení aktivních, admin spravuje) ----

    suspend fun fetchChildren(): List<SgChildDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child") {
            sgAuth(null)
            parameter("select", "id,display_name,nickname,photo_url,bio_html,is_active,sort_order")
            parameter("is_active", "eq.true")
            parameter("order", "sort_order")
        }.body()

    /** Všechny děti (i neaktivní) pro admin editaci. */
    suspend fun fetchChildrenAdmin(token: String): List<SgChildDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child") {
            sgAuth(token)
            parameter("select", "id,display_name,nickname,photo_url,bio_html,is_active,sort_order")
            parameter("order", "sort_order")
        }.body()

    /** Vytvoří (id == null) nebo upraví (id zadané) dítě. Vrátí id. */
    suspend fun upsertChild(
        token: String, id: String?, displayName: String, nickname: String?,
        photoUrl: String?, bioHtml: String, isActive: Boolean, sortOrder: Int,
    ): String {
        if (id == null) {
            return httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child") {
                sgAuth(token)
                header("Prefer", "return=representation")
                contentType(ContentType.Application.Json)
                setBody(listOf(ChildUpsertInput(null, displayName, nickname?.ifBlank { null }, photoUrl?.ifBlank { null }, bioHtml, isActive, sortOrder)))
            }.body<List<SgChildDto>>().first().id
        }
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_child?id=eq.$id") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(ChildUpsertInput(id, displayName, nickname?.ifBlank { null }, photoUrl?.ifBlank { null }, bioHtml, isActive, sortOrder))
        }
        return id
    }

    // ---- tréninky dětí (soukromé) ----

    suspend fun fetchChildTrainings(token: String): List<SgChildTrainingDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_training") {
            sgAuth(token)
            parameter("select", "id,date,location,notes")
            parameter("order", "date.desc")
        }.body()

    suspend fun createChildTraining(token: String, createdBy: String, date: String, location: String?, notes: String?): String =
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_training") {
            sgAuth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(listOf(CreateChildTrainingInput(date, location?.ifBlank { null }, notes?.ifBlank { null }, createdBy)))
        }.body<List<SgChildTrainingDto>>().first().id

    suspend fun updateChildTraining(token: String, id: String, date: String, notes: String?) {
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_child_training?id=eq.$id") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("date" to date, "notes" to notes?.ifBlank { null }))
        }
    }

    suspend fun deleteChildTraining(token: String, id: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_training?id=eq.$id") { sgAuth(token) }
    }

    suspend fun fetchChildTrainingScores(token: String, trainingIds: List<String>): List<SgChildScoreDto> {
        if (trainingIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_training_score") {
            sgAuth(token)
            parameter("select", "training_id,child_id,game_no,score,game_type")
            parameter("training_id", "in.(${trainingIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildTrainingScores(token: String, trainingId: String, entries: List<ChildScoreEntry>) {
        if (entries.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_training_score") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(entries.map { ChildTrainingScoreInput(trainingId, it.childId, it.gameNo, it.score, it.gameType) })
        }
    }

    suspend fun deleteChildTrainingScores(token: String, trainingId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_training_score?training_id=eq.$trainingId") { sgAuth(token) }
    }

    suspend fun fetchChildTrainingSheets(token: String, trainingIds: List<String>): List<SgChildScoresheetDto> {
        if (trainingIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet") {
            sgAuth(token)
            parameter("select", "training_id,child_id,game_no,game_type,frames,total")
            parameter("training_id", "in.(${trainingIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildTrainingSheets(token: String, trainingId: String, createdBy: String, sheets: List<ChildSheetEntry>) {
        if (sheets.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(sheets.map { ChildTrainingSheetInput("child_training", trainingId, it.childId, it.gameNo, it.gameType, it.frames, it.total, createdBy) })
        }
    }

    suspend fun deleteChildTrainingSheets(token: String, trainingId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet?training_id=eq.$trainingId") { sgAuth(token) }
    }

    // ---- kola dětské ligy (veřejné čtení, členové zapisují) ----

    suspend fun fetchChildRounds(token: String? = null): List<SgChildRoundDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_round") {
            sgAuth(token)
            parameter("select", "id,date,round_no,location,notes")
            parameter("order", "date.desc")
        }.body()

    suspend fun createChildRound(token: String, createdBy: String, date: String, roundNo: Int?, location: String?, notes: String?): String =
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_round") {
            sgAuth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(listOf(CreateChildRoundInput(date, roundNo, location?.ifBlank { null }, notes?.ifBlank { null }, createdBy)))
        }.body<List<SgChildRoundDto>>().first().id

    suspend fun updateChildRound(token: String, id: String, date: String, roundNo: Int?, notes: String?) {
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_child_round?id=eq.$id") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(UpdateChildRoundInput(date, roundNo, notes?.ifBlank { null }))
        }
    }

    suspend fun deleteChildRound(token: String, id: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_round?id=eq.$id") { sgAuth(token) }
    }

    suspend fun fetchChildRoundScores(token: String? = null, roundIds: List<String>): List<SgChildScoreDto> {
        if (roundIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_round_score") {
            sgAuth(token)
            parameter("select", "round_id,child_id,game_no,score,game_type")
            parameter("round_id", "in.(${roundIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildRoundScores(token: String, roundId: String, entries: List<ChildScoreEntry>) {
        if (entries.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_round_score") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(entries.map { ChildRoundScoreInput(roundId, it.childId, it.gameNo, it.score, it.gameType) })
        }
    }

    suspend fun deleteChildRoundScores(token: String, roundId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_round_score?round_id=eq.$roundId") { sgAuth(token) }
    }

    suspend fun fetchChildRoundPlacements(token: String? = null, roundIds: List<String>): List<SgChildRoundPlacementDto> {
        if (roundIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_round_placement") {
            sgAuth(token)
            parameter("select", "round_id,child_id,placement")
            parameter("round_id", "in.(${roundIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildRoundPlacements(token: String, roundId: String, placements: List<ChildPlacementEntry>) {
        if (placements.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_round_placement") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(placements.map { ChildRoundPlacementInput(roundId, it.childId, it.placement) })
        }
    }

    suspend fun deleteChildRoundPlacements(token: String, roundId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_round_placement?round_id=eq.$roundId") { sgAuth(token) }
    }

    suspend fun fetchChildRoundSheets(token: String, roundIds: List<String>): List<SgChildScoresheetDto> {
        if (roundIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet") {
            sgAuth(token)
            parameter("select", "round_id,child_id,game_no,game_type,frames,total")
            parameter("round_id", "in.(${roundIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildRoundSheets(token: String, roundId: String, createdBy: String, sheets: List<ChildSheetEntry>) {
        if (sheets.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(sheets.map { ChildRoundSheetInput("child_round", roundId, it.childId, it.gameNo, it.gameType, it.frames, it.total, createdBy) })
        }
    }

    suspend fun deleteChildRoundSheets(token: String, roundId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet?round_id=eq.$roundId") { sgAuth(token) }
    }

    // ---- speciální turnaje dětí (veřejné čtení, členové zapisují) ----

    suspend fun fetchChildTournaments(token: String? = null): List<SgChildTournamentDto> =
        httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_tournament") {
            sgAuth(token)
            parameter("select", "id,name,date,location,notes,game_type")
            parameter("order", "date.desc")
        }.body()

    suspend fun createChildTournament(token: String, createdBy: String, name: String, date: String, gameType: String, notes: String?): String =
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_tournament") {
            sgAuth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(listOf(CreateChildTournamentInput(name, date, gameType, null, notes?.ifBlank { null }, createdBy)))
        }.body<List<SgChildTournamentDto>>().first().id

    suspend fun updateChildTournament(token: String, id: String, name: String, date: String, gameType: String, notes: String?) {
        httpClient.patch("$SG_SUPABASE_URL/rest/v1/sg_child_tournament?id=eq.$id") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("name" to name, "date" to date, "game_type" to gameType, "notes" to notes?.ifBlank { null }))
        }
    }

    suspend fun deleteChildTournament(token: String, id: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_tournament?id=eq.$id") { sgAuth(token) }
    }

    suspend fun fetchChildTournamentScores(token: String? = null, tournamentIds: List<String>): List<SgChildScoreDto> {
        if (tournamentIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_tournament_score") {
            sgAuth(token)
            parameter("select", "tournament_id,child_id,game_no,score,game_type")
            parameter("tournament_id", "in.(${tournamentIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildTournamentScores(token: String, tournamentId: String, entries: List<ChildScoreEntry>) {
        if (entries.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_tournament_score") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(entries.map { ChildTournamentScoreInput(tournamentId, it.childId, it.gameNo, it.score, it.gameType) })
        }
    }

    suspend fun deleteChildTournamentScores(token: String, tournamentId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_tournament_score?tournament_id=eq.$tournamentId") { sgAuth(token) }
    }

    suspend fun fetchChildTournamentEntries(token: String? = null, tournamentIds: List<String>): List<SgChildTournamentEntryDto> {
        if (tournamentIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_tournament_entry") {
            sgAuth(token)
            parameter("select", "tournament_id,child_id,placement,partner_note")
            parameter("tournament_id", "in.(${tournamentIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildTournamentEntries(token: String, tournamentId: String, entries: List<ChildTournamentEntryEntry>) {
        if (entries.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_tournament_entry") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(entries.map { ChildTournamentEntryInput(tournamentId, it.childId, it.placement, it.partnerNote?.ifBlank { null }) })
        }
    }

    suspend fun deleteChildTournamentEntries(token: String, tournamentId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_tournament_entry?tournament_id=eq.$tournamentId") { sgAuth(token) }
    }

    suspend fun fetchChildTournamentSheets(token: String, tournamentIds: List<String>): List<SgChildScoresheetDto> {
        if (tournamentIds.isEmpty()) return emptyList()
        return httpClient.get("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet") {
            sgAuth(token)
            parameter("select", "tournament_id,child_id,game_no,game_type,frames,total")
            parameter("tournament_id", "in.(${tournamentIds.joinToString(",")})")
        }.body()
    }

    suspend fun addChildTournamentSheets(token: String, tournamentId: String, createdBy: String, sheets: List<ChildSheetEntry>) {
        if (sheets.isEmpty()) return
        httpClient.post("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet") {
            sgAuth(token)
            contentType(ContentType.Application.Json)
            setBody(sheets.map { ChildTournamentSheetInput("child_tournament", tournamentId, it.childId, it.gameNo, it.gameType, it.frames, it.total, createdBy) })
        }
    }

    suspend fun deleteChildTournamentSheets(token: String, tournamentId: String) {
        httpClient.delete("$SG_SUPABASE_URL/rest/v1/sg_child_scoresheet?tournament_id=eq.$tournamentId") { sgAuth(token) }
    }
}
